/**
 * Action & toolbar icons — the icon-only controls across the app (close, copy, export,
 * filter, refresh, favorite, playback transport, send, sort caret, calendar). Line-only,
 * single-silhouette, on the 24 grid. Every icon-only control keeps its own aria-label on
 * the BUTTON; these glyphs stay aria-hidden (the primitive's default).
 *
 * Only concepts with a confirmed real consumer live here. Speculative icons (zoom/pan,
 * clock, notifications-bell, layers, table/chart toggle) are intentionally omitted — add
 * them when a real surface needs them, so the set never drifts ahead of use.
 */
import { Icon, type IconProps } from './Icon';

/** Close / dismiss — a bare X. */
export const Close = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M6 6L18 18M18 6L6 18" />
  </Icon>
);

/** Copy — two offset rounded rects. */
export const Copy = (props: IconProps) => (
  <Icon size="xs" {...props}>
    <rect x="8" y="8" width="13" height="13" rx="2" />
    <path d="M4 16V5a2 2 0 0 1 2-2h9" />
  </Icon>
);

/** Export / download — tray + down arrow. */
export const Download = (props: IconProps) => (
  <Icon size="xs" {...props}>
    <path d="M12 3V15M12 15L8 11M12 15L16 11" />
    <path d="M4 17V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V17" />
  </Icon>
);
/** Alias: the export affordance is the same glyph as download. */
export const Export = Download;

/** Filter — a funnel. */
export const Filter = (props: IconProps) => (
  <Icon {...props}>
    <path d="M3 5h18l-7 8v6l-4 2v-8L3 5z" />
  </Icon>
);

/** Refresh / retry — circular arrows. */
export const Refresh = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v4.5H16.5" />
  </Icon>
);

/**
 * Favorite — a star. Outline at rest; consumers toggle the fill by passing
 * fill={active ? 'currentColor' : 'none'} (the primitive sets fill before the spread, so
 * the override wins). This preserves the existing FavoriteToggle / QuickAccessRail pattern.
 */
export const Favorite = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M12 3.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L12 16.77l-5.2 2.75.99-5.8-4.21-4.1 5.82-.85L12 3.5z" />
  </Icon>
);

// ── Playback transport ─────────────────────────────────────────────────────
/** Play — triangle. */
export const Play = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M7 5l12 7-12 7V5z" fill="currentColor" stroke="none" />
  </Icon>
);
/** Pause — two bars. */
export const Pause = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" />
    <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" stroke="none" />
  </Icon>
);
/** Stop — square. */
export const Stop = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" />
  </Icon>
);
/** Step forward — bar + triangle. */
export const StepForward = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M6 5l10 7-10 7V5z" fill="currentColor" stroke="none" />
    <rect x="17" y="5" width="2.5" height="14" rx="1" fill="currentColor" stroke="none" />
  </Icon>
);
/** Step backward — triangle + bar. */
export const StepBackward = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M18 5L8 12l10 7V5z" fill="currentColor" stroke="none" />
    <rect x="4.5" y="5" width="2.5" height="14" rx="1" fill="currentColor" stroke="none" />
  </Icon>
);

/** Send (NORA chat) — a paper plane. */
export const Send = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <path d="M21 3L10.5 13.5M21 3l-6.5 18-4-8-8-4L21 3z" />
  </Icon>
);

/** Calendar — date grid. */
export const Calendar = (props: IconProps) => (
  <Icon size="sm" {...props}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </Icon>
);

/**
 * Sort caret — a single chevron. `direction` picks up/down (default down). Table sort
 * indicators pass the `.sort-icon`/`.sort-icon active` class + rotate via CSS, so this stays
 * class-free; where a component switches glyphs by state it can flip `direction` instead.
 */
export const Sort = ({ direction = 'down', ...props }: IconProps & { direction?: 'up' | 'down' }) => (
  <Icon size="xs" {...props}>
    {direction === 'up' ? <path d="M6 15l6-6 6 6" /> : <path d="M6 9l6 6 6-6" />}
  </Icon>
);
